import React, { useState } from 'react';
import { User, UserGroup, Venue } from '@/types';
import { MapPin, PenTool, Trash2, X } from 'lucide-react';
import { showAlertModal } from '@shared/components/common/AlertModal';
import { createId } from '@/lib/ids';
import { validateEmail } from '@/lib/validators';
import { useUserMutations } from '@features/admin/data';

const deriveRoleFromGroup = (userGroupId: string | undefined, groups: UserGroup[]) => {
  if (!userGroupId) return 'staff';
  if (userGroupId === 'ug_tech') return 'technician';
  const group = groups.find((g) => g.id === userGroupId);
  if (group?.type === 'admin' || group?.type === 'global_admin') return 'admin';
  return 'staff';
};

const SignatureCanvas = ({
  onSave,
  onCancel,
  initialDataUrl
}: {
  onSave: (dataUrl: string) => void;
  onCancel: () => void;
  initialDataUrl?: string;
}) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = React.useState(false);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#000';
      }
    }
  }, []);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !initialDataUrl) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
      const drawWidth = img.width * scale;
      const drawHeight = img.height * scale;
      ctx.drawImage(
        img,
        (canvas.width - drawWidth) / 2,
        (canvas.height - drawHeight) / 2,
        drawWidth,
        drawHeight
      );
    };
    img.src = initialDataUrl;
  }, [initialDataUrl]);

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x =
      'touches' in e
        ? e.touches[0].clientX - rect.left
        : (e as React.MouseEvent).nativeEvent.offsetX;
    const y =
      'touches' in e
        ? e.touches[0].clientY - rect.top
        : (e as React.MouseEvent).nativeEvent.offsetY;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x =
      'touches' in e
        ? e.touches[0].clientX - rect.left
        : (e as React.MouseEvent).nativeEvent.offsetX;
    const y =
      'touches' in e
        ? e.touches[0].clientY - rect.top
        : (e as React.MouseEvent).nativeEvent.offsetY;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl mb-4 relative h-56 overflow-hidden shadow-inner">
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={() => setIsDrawing(false)}
          onMouseLeave={() => setIsDrawing(false)}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={() => setIsDrawing(false)}
        />
        <div className="absolute top-2 right-2">
          <button
            onClick={clearCanvas}
            className="bg-white/80 p-2 rounded-full hover:bg-white shadow-sm text-slate-500"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded font-bold"
        >
          Cancel
        </button>
        <button
          onClick={() => canvasRef.current && onSave(canvasRef.current.toDataURL())}
          className="bg-slate-900 text-white px-6 py-2 rounded-xl font-black hover:bg-slate-800 transition-all flex items-center gap-2 uppercase tracking-wide text-xs"
        >
          Save Signature
        </button>
      </div>
    </div>
  );
};

interface UserModalProps {
  user: Partial<User>;
  groups: UserGroup[];
  venues: Venue[];
  currentUserGroup: UserGroup;
  isExisting: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export const UserModal: React.FC<UserModalProps> = ({
  user,
  groups,
  venues,
  currentUserGroup,
  isExisting,
  onClose,
  onSaved
}) => {
  const userMutations = useUserMutations();
  const [formData, setFormData] = useState<Partial<User>>({
    name: user.name || '',
    email: user.email || '',
    role: deriveRoleFromGroup(user.userGroupId, groups),
    userGroupId: user.userGroupId || '',
    assignedVenueIds: user.assignedVenueIds || [],
    id: user.id || undefined,
    signature: user.signature || undefined
  });
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);

  const availableGroups = groups.filter((g) => {
    if (currentUserGroup.type !== 'global_admin' && g.type === 'global_admin') return false;
    return true;
  });

  const toggleVenueAssignment = (venueId: string) => {
    const current = formData.assignedVenueIds || [];
    setFormData({
      ...formData,
      assignedVenueIds: current.includes(venueId)
        ? current.filter((id) => id !== venueId)
        : [...current, venueId]
    });
  };

  const handleSave = async () => {
    const cleanEmail = String(formData.email || '').trim();
    if (!formData.name || !cleanEmail || !formData.userGroupId) {
      showAlertModal('Please complete the required fields (Name, Email, and Group).');
      return;
    }
    if (!validateEmail(cleanEmail).valid) {
      showAlertModal('Please enter a valid email address.');
      return;
    }
    const role = deriveRoleFromGroup(formData.userGroupId, groups);
    const payload = { ...formData, email: cleanEmail, role, id: formData.id || createId() } as User;
    const signatureChanged =
      typeof payload.signature === 'string' && payload.signature !== user.signature;
    if (isExisting) {
      await userMutations.update(payload);
    } else {
      await userMutations.create(payload);
    }
    if (signatureChanged) showAlertModal('Signature saved.');
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      {isSignatureModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-scale-up overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center bg-slate-50">
              <h3 className="font-black text-slate-800 flex items-center gap-2 uppercase tracking-tight text-sm">
                <PenTool className="w-4 h-4 text-teal-600" /> Signature Capture
              </h3>
              <button onClick={() => setIsSignatureModalOpen(false)}>
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6">
              <SignatureCanvas
                onSave={(url) => {
                  setFormData((prev) => ({ ...prev, signature: url }));
                  setIsSignatureModalOpen(false);
                }}
                onCancel={() => setIsSignatureModalOpen(false)}
                initialDataUrl={formData.signature}
              />
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl p-6 max-md w-full animate-fade-in max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold text-slate-800">
            {formData.id ? 'Edit Staff Member' : 'Add New Staff Member'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
              Full Name
            </label>
            <input
              className="w-full border rounded-lg p-2.5 bg-white text-slate-900 outline-none focus:ring-2 focus:ring-teal-500 transition-all"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. John Smith"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
              Email Address
            </label>
            <input
              type="email"
              className="w-full border rounded-lg p-2.5 bg-white text-slate-900 outline-none focus:ring-2 focus:ring-teal-500 transition-all"
              value={formData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="name@k2occuhealth.com"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
              Permission Group
            </label>
            <select
              className="w-full border rounded-lg p-2.5 bg-white text-slate-900 outline-none focus:ring-2 focus:ring-teal-500 transition-all"
              value={formData.userGroupId || ''}
              onChange={(e) => {
                const groupId = e.target.value;
                setFormData({
                  ...formData,
                  userGroupId: groupId,
                  role: deriveRoleFromGroup(groupId, groups)
                });
              }}
            >
              <option value="" disabled>
                Select access level...
              </option>
              {availableGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>

          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
            <label className="block text-xs font-bold text-slate-500 uppercase mb-3 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-teal-600" /> Site Authorization
            </label>
            <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
              {venues.map((v) => (
                <label
                  key={v.id}
                  className="flex items-center gap-3 cursor-pointer p-2 hover:bg-white rounded-lg transition-colors group border border-transparent hover:border-slate-200"
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-teal-600 rounded"
                    checked={formData.assignedVenueIds?.includes(v.id)}
                    onChange={() => toggleVenueAssignment(v.id)}
                  />
                  <span className="text-sm text-slate-700 group-hover:text-teal-700">{v.name}</span>
                </label>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-3 italic leading-relaxed">
              If no sites are selected, this user will have access to all system locations by
              default.
            </p>
          </div>

          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
            <label className="block text-xs font-bold text-slate-500 uppercase mb-3 flex items-center gap-2">
              <PenTool className="w-3.5 h-3.5 text-teal-600" /> Digital Signature
            </label>
            {formData.signature ? (
              <div className="bg-white border border-dashed border-slate-200 rounded-lg p-3 flex items-center justify-center mb-3">
                <img
                  src={formData.signature}
                  alt="User signature"
                  className="max-h-24 w-auto object-contain"
                />
              </div>
            ) : (
              <p className="text-[10px] text-slate-400 mb-3 italic">No signature on file.</p>
            )}
            <button
              type="button"
              onClick={() => setIsSignatureModalOpen(true)}
              className="bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all"
            >
              {formData.signature ? 'Update Signature' : 'Add Signature'}
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-6 py-2 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-slate-900 text-white px-8 py-2 rounded-xl font-bold hover:bg-slate-800 transition-all shadow-lg active:scale-95"
          >
            {formData.id ? 'Update Member' : 'Create Member'}
          </button>
        </div>
      </div>
    </div>
  );
};
